import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AllowedEmailDomain,
  AllowedTenantId,
  AllowedTenantIdsResponse,
  Annotation,
  ApproveCostBudgetVersion,
  AuthStatus,
  Automation,
  AutomationRun,
  BasemapDescriptor,
  BasemapSettings,
  CatalogEntryWire,
  ChangeMembership,
  CostAccount,
  CostBudgetLine,
  CostBudgetVersion,
  CostEntry,
  CostForecastLine,
  CostForecastVersion,
  CostImport,
  CostImportResult,
  CostOverview,
  CostSettings,
  CostSource,
  CostSummary,
  CostTimeseries,
  CreateAllowedEmailDomain,
  CreateAllowedTenantId,
  CreateCostAccount,
  CreateCostBudgetLine,
  CreateCostBudgetVersion,
  CreateCostEntry,
  CreateCostForecastVersion,
  CreateCostSource,
  CreateCostSourceClaim,
  CreateEntityLink,
  CreateIntegration,
  CreateMapFeature,
  CreateProject,
  CreateRole,
  CreateScheduleDependency,
  CreateScheduleTask,
  CreateView,
  DecideScheduleSession,
  Deployment,
  DeploymentReadiness,
  EntityLink,
  EntityLinkList,
  EntityRef,
  GrantPlatformAdmin,
  InfrastructureAsset,
  Integration,
  IntegrationHealth,
  IntegrationProviderCatalogEntry,
  IntegrationTestResult,
  InviteUser,
  IssueCostForecastVersion,
  Job,
  LinkCandidateList,
  LinkEntityKind,
  MapFeatureCollection,
  MapFeatureDetail,
  MapLayerKey,
  MapOverview,
  MapSearchResult,
  MetricKey,
  MetricResult,
  MoveCostAccount,
  Overview,
  Page,
  Permission,
  PlatformAdmin,
  Project,
  ProjectMapSettings,
  PublishScheduleTask,
  QueueHealth,
  ReplaceCostForecastLines,
  Role,
  ScheduleCalendar,
  ScheduleChangeSession,
  ScheduleDependency,
  ScheduleInboxSummary,
  ScheduleNotification,
  ScheduleSessionPreview,
  ScheduleSummary,
  ScheduleTask,
  ScheduleTaskPatch,
  SubmitScheduleSession,
  TimeSeries,
  UpdateBasemapSettings,
  UpdateCostAccount,
  UpdateCostBudgetLine,
  UpdateCostEntry,
  UpdateCostSettings,
  UpdateCostSource,
  UpdateDeploymentIdentity,
  UpdateDeploymentModules,
  UpdateDeploymentStage,
  UpdateDeploymentTheme,
  UpdateEntityLink,
  UpdateIntegration,
  UpdateMapFeature,
  UpdateOwnProfile,
  UpdateProject,
  UpdateProjectMapSettings,
  UpdateRole,
  UpdateScheduleCalendar,
  UpdateScheduleTask,
  UpdateScheduleTaskStatus,
  BatchScheduleProgressReport,
  CreateScheduleProgressReport,
  CostEarnedValue,
  ScheduleCostLinks,
  ScheduleProgressCurve,
  ScheduleProgressReport,
  UpdateScheduleProgressSettings,
  UpdateUser,
  UpdateView,
  User,
  View,
  ViewSummary,
  VoidCostEntry,
} from '@dashboard/contracts';
import { apiRequest } from '../client';
import { useCompleteQuery } from '../pagination';

/**
 * Query keys are structured so that a targeted invalidation is possible without
 * a string convention everyone has to remember: ['infrastructure-assets', 'list', filters]
 * invalidates by prefix.
 */
export const queryKeys = {
  authStatus: ['auth', 'status'] as const,
  views: ['views'] as const,
  view: (slug: string) => ['views', slug] as const,
  catalog: ['component-catalog'] as const,
  project: (projectId: string) => ['projects', projectId] as const,
  services: (filters: Record<string, unknown> = {}) =>
    ['infrastructure-assets', 'list', filters] as const,
  timeSeries: (id: string, window: string) => ['services', id, 'time-series', window] as const,
  metrics: (metrics: MetricKey[], environment?: string) =>
    ['dashboard', 'metrics', [...metrics].sort().join(','), environment ?? 'all'] as const,
  overview: ['dashboard', 'overview'] as const,
  costOverview: (projectId: string, filters: Record<string, unknown> = {}) =>
    ['costs', projectId, 'overview', filters] as const,
  costSummary: (projectId: string) => ['costs', projectId, 'summary'] as const,
  costTimeseries: (projectId: string, filters: Record<string, unknown> = {}) =>
    ['costs', projectId, 'timeseries', filters] as const,
  costAccounts: (projectId: string) => ['costs', projectId, 'accounts'] as const,
  costEntries: (projectId: string, filters: Record<string, unknown> = {}) =>
    ['costs', projectId, 'entries', filters] as const,
  documentGroupSuggestions: (projectId: string, q: string) =>
    ['costs', projectId, 'entries', 'document-groups', q] as const,
  costBudgetVersions: (projectId: string) => ['costs', projectId, 'budget-versions'] as const,
  costBudgetLines: (projectId: string, versionId: string) =>
    ['costs', projectId, 'budget-versions', versionId, 'lines'] as const,
  costForecastVersions: (projectId: string) => ['costs', projectId, 'forecast-versions'] as const,
  costForecastLines: (projectId: string, versionId: string) =>
    ['costs', projectId, 'forecast-versions', versionId, 'lines'] as const,
  costSources: (projectId: string) => ['costs', projectId, 'sources'] as const,
  costSettings: (projectId: string) => ['costs', projectId, 'settings'] as const,
  /* Under `schedule`, not `costs`: it is read by the schedule's task panel and
     invalidated by the schedule's own writes, and a key filed by where the data
     is stored rather than by who reads it is the one that gets missed. */
  scheduleCostLinks: (projectId: string) => ['schedule', projectId, 'cost-links'] as const,
  jobs: (filters: Record<string, unknown> = {}) => ['jobs', 'list', filters] as const,
  queueHealth: ['jobs', 'queue-health'] as const,
  integrations: ['integrations'] as const,
  integrationHealth: ['integrations', 'health'] as const,
  integrationProviders: ['integrations', 'providers'] as const,
  automations: ['automations'] as const,
  automationRuns: ['automations', 'runs'] as const,
  annotations: (filters: Record<string, unknown> = {}) => ['annotations', filters] as const,
  scheduleTasks: (projectId: string, filters: Record<string, unknown> = {}) =>
    ['schedule', projectId, 'tasks', filters] as const,
  scheduleSummary: (projectId: string) => ['schedule', projectId, 'summary'] as const,
  scheduleDependencies: (projectId: string) => ['schedule', projectId, 'dependencies'] as const,
  scheduleCalendar: (projectId: string) => ['schedule', projectId, 'calendar'] as const,
  scheduleSession: (projectId: string) => ['schedule', projectId, 'session'] as const,
  scheduleSessionPreview: (projectId: string, sessionId: string) =>
    ['schedule', projectId, 'session', sessionId, 'preview'] as const,
  /* The inbox is a person's, not a project's, so it sits outside the
   * ['schedule', projectId] tree that schedule mutations invalidate. */
  scheduleNotifications: ['schedule-inbox', 'notifications'] as const,
  scheduleInboxSummary: ['schedule-inbox', 'summary'] as const,
  /*
   * Keyed by project, then by what varies. The basemap descriptor sits outside
   * that tree entirely: it is deployment configuration, identical for every
   * project, and nesting it under one would refetch it whenever project data
   * was invalidated.
   */
  mapOverview: (projectId: string) => ['map', projectId, 'overview'] as const,
  mapFeatures: (projectId: string, filters: Record<string, unknown> = {}) =>
    ['map', projectId, 'features', filters] as const,
  mapFeature: (projectId: string, featureId: string) =>
    ['map', projectId, 'features', featureId] as const,
  /*
   * Keyed by the item the links belong to, not by a project. A cross-reference
   * list is a property of one item and is invalidated when that item's links
   * change -- including from the other end, which is why every link mutation
   * invalidates the whole `links` prefix rather than one anchor: the far end's
   * list changed too, and it is keyed under an id the mutation does not
   * necessarily know.
   */
  entityLinks: (kind: string, id: string) => ['links', kind, id] as const,
  linkCandidates: (kind: string, filters: Record<string, unknown> = {}) =>
    ['links', 'candidates', kind, filters] as const,
  mapSearch: (projectId: string, term: string) => ['map', projectId, 'search', term] as const,
  /*
   * Keyed by project because the descriptor's opening camera is now the
   * project's, resolved by the API. Everything else in it is deployment-wide,
   * so two projects fetch two near-identical documents -- which is one small
   * request each against a cache that lasts half an hour, and the alternative
   * was a second query and a frame of the wrong camera while it landed.
   */
  basemap: (projectId?: string) => ['map', 'basemap', projectId ?? null] as const,
  projectMapSettings: (projectId: string) => ['map', projectId, 'settings'] as const,
  basemapSettings: ['map', 'basemap-settings'] as const,
  users: ['users'] as const,
  /* Nested under 'users' so inviting or editing someone also invalidates the
   * unfiltered picker list `useUsers` keeps for owner/watcher selects. */
  usersPage: (filters: Record<string, unknown> = {}) => ['users', 'page', filters] as const,
  roles: ['roles'] as const,
  permissionCatalog: ['permissions'] as const,
  deployment: ['platform', 'deployment'] as const,
  deploymentReadiness: ['platform', 'deployment', 'readiness'] as const,
  allowedEmailDomains: ['platform', 'access-control', 'email-domains'] as const,
  allowedTenantIds: ['platform', 'access-control', 'tenant-ids'] as const,
  platformAdmins: ['platform', 'admins'] as const,
};

/*
 * Query defaults live in `./shared`, so a per-feature query module can use them
 * without importing this barrel -- which re-exports those modules, and would
 * therefore be a cycle. Re-exported here because everything already imports
 * them from `../api/queries`.
 */
import { defaultQueryOptions, shouldRetry } from './shared';

export { defaultQueryOptions, shouldRetry };

/*
 * Per-feature query modules. The first step of splitting this file: it is two
 * thousand lines and a hundred and thirty hooks covering every feature in the
 * product, so every feature branch edits it and every feature branch conflicts
 * in it. New features get their own module; the rest move as they are next
 * touched.
 */
export * from './access-windows';
export * from './baselines';
export * from './linear-referencing';
export * from './infrastructure-asset-types';
export * from './commercial';
export * from './organizations';
export * from './documents';
export * from './cost-import';
export * from './schedule-import';
export * from './map-import';
export * from './map-layers';
export * from './map-views';
export * from './fleet';
export * from './project-structure';
export * from './notifications';
export * from './subscriptions';

export function useAuthStatus() {
  return useQuery({
    queryKey: queryKeys.authStatus,
    queryFn: ({ signal }) => apiRequest<AuthStatus>('/api/v1/auth/status', { signal }),
    ...defaultQueryOptions,
    staleTime: 60_000,
  });
}

export function useViews() {
  return useQuery({
    queryKey: queryKeys.views,
    queryFn: ({ signal }) => apiRequest<ViewSummary[]>('/api/v1/views', { signal }),
    ...defaultQueryOptions,
  });
}

export function useView(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.view(slug ?? ''),
    queryFn: ({ signal }) => apiRequest<View>(`/api/v1/views/${slug}`, { signal }),
    enabled: Boolean(slug),
    ...defaultQueryOptions,
  });
}

export function useComponentCatalog() {
  return useQuery({
    queryKey: queryKeys.catalog,
    queryFn: ({ signal }) =>
      apiRequest<{ components: CatalogEntryWire[] }>('/api/v1/component-catalog', { signal }),
    // The catalog is static for the lifetime of a deployment.
    staleTime: Infinity,
    retry: shouldRetry,
  });
}

export function useUpdateView() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; slug: string; body: UpdateView }) =>
      apiRequest<View>(`/api/v1/views/${input.id}`, { method: 'PATCH', body: input.body }),
    onSuccess: (view) => {
      client.setQueryData(queryKeys.view(view.slug), view);
      void client.invalidateQueries({ queryKey: queryKeys.views });
    },
  });
}

/**
 * Component data hooks.
 *
 * Each takes a `refetchInterval` from the component's declared refresh policy
 * rather than hard-coding one, so polling cadence is a property of the catalog
 * entry (section 8.3) instead of a number buried in a renderer.
 */
export function useMetrics(metrics: MetricKey[], environment?: string, refetchInterval?: number) {
  return useQuery({
    queryKey: queryKeys.metrics(metrics, environment),
    queryFn: ({ signal }) =>
      apiRequest<{ results: MetricResult[] }>('/api/v1/dashboard/metrics', {
        signal,
        query: { metrics: metrics.join(','), environment },
      }),
    enabled: metrics.length > 0,
    ...defaultQueryOptions,
    refetchInterval,
  });
}

export function useOverview() {
  return useQuery({
    queryKey: queryKeys.overview,
    queryFn: ({ signal }) => apiRequest<Overview>('/api/v1/dashboard/overview', { signal }),
    ...defaultQueryOptions,
  });
}

/**
 * `enabled` exists for callers that decide at render time whether they want the
 * list at all -- the entity picker switches its request off for a field it is
 * not currently resolving, rather than issuing one nobody reads, and a hook
 * cannot be called conditionally.
 */
export function useInfrastructureAssets(
  filters: {
    environment?: string;
    status?: string;
    typeId?: string;
    search?: string;
    limit?: number;
  } = {},
  refetchInterval?: number,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.services(filters),
    queryFn: ({ signal }) =>
      apiRequest<Page<InfrastructureAsset>>('/api/v1/infrastructure-assets', {
        signal,
        query: { ...filters },
      }),
    enabled,
    ...defaultQueryOptions,
    refetchInterval,
  });
}

export function useTimeSeries(serviceId: string, window: string, refetchInterval?: number) {
  return useQuery({
    queryKey: queryKeys.timeSeries(serviceId, window),
    queryFn: ({ signal }) =>
      apiRequest<TimeSeries>(`/api/v1/infrastructure-assets/${serviceId}/time-series`, {
        signal,
        query: { window },
      }),
    enabled: Boolean(serviceId),
    ...defaultQueryOptions,
    refetchInterval,
  });
}

/**
 * One project by identifier, which is the only shape the frontend asks for.
 *
 * There is no list hook. The workspace's project arrives with the session
 * context and every screen is scoped to it (see `project/active-project`), so
 * nothing in the interface has a reason to enumerate projects -- while
 * `GET /api/v1/projects` stays where it is, because that is the seam a
 * portfolio view would read.
 */
export function useProjectDetail(projectId: string | undefined, refetchInterval?: number) {
  return useQuery({
    queryKey: queryKeys.project(projectId ?? ''),
    queryFn: ({ signal }) => apiRequest<Project>(`/api/v1/projects/${projectId}`, { signal }),
    // Without this an absent id would request `/api/v1/projects/`, which is the
    // list endpoint answering with a page where a project was expected.
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
    refetchInterval,
  });
}

export function useJobs(
  filters: { status?: string } = {},
  enabled = true,
  refetchInterval?: number,
) {
  return useQuery({
    queryKey: queryKeys.jobs(filters),
    queryFn: ({ signal }) =>
      apiRequest<Page<Job>>('/api/v1/jobs', { signal, query: { ...filters } }),
    enabled,
    ...defaultQueryOptions,
    refetchInterval,
  });
}

/**
 * `refetchInterval` is optional and falls back to the cadence the operations
 * page has always used. A dashboard tile passes the interval its own catalog
 * entry declares, which is what makes that declaration true rather than
 * decorative -- the same reasoning the register tile's comment gives.
 */
export function useQueueHealth(refetchInterval = 30_000) {
  return useQuery({
    queryKey: queryKeys.queueHealth,
    queryFn: ({ signal }) => apiRequest<QueueHealth>('/api/v1/jobs/queue-health', { signal }),
    ...defaultQueryOptions,
    refetchInterval,
  });
}

/* -------------------------------------------------------------------------- */
/* Integrations, automations and annotations                                   */
/* -------------------------------------------------------------------------- */

export function useIntegrations(enabled = true) {
  return useQuery({
    queryKey: queryKeys.integrations,
    queryFn: ({ signal }) =>
      apiRequest<Page<Integration>>('/api/v1/integrations', { signal, query: { limit: 100 } }),
    enabled,
    ...defaultQueryOptions,
  });
}

export function useIntegrationHealth(enabled = true, refetchInterval = 60_000) {
  return useQuery({
    queryKey: queryKeys.integrationHealth,
    queryFn: ({ signal }) =>
      apiRequest<IntegrationHealth[]>('/api/v1/integrations/health', { signal }),
    enabled,
    ...defaultQueryOptions,
    refetchInterval,
  });
}

/**
 * Every registered provider, settings schema included -- what the create/edit
 * dialog generates its provider list and settings form from
 * (`integration-edit-dialog.tsx`), instead of a field block hand-written per
 * provider. Rarely changes (a new provider is a deployment, not a user
 * action), so this is closer to `usePermissionCatalog` than to the
 * frequently-refetched health/status queries above.
 */
export function useIntegrationProviderCatalog(enabled = true) {
  return useQuery({
    queryKey: queryKeys.integrationProviders,
    queryFn: ({ signal }) =>
      apiRequest<IntegrationProviderCatalogEntry[]>('/api/v1/integrations/providers', { signal }),
    enabled,
    ...defaultQueryOptions,
  });
}

/**
 * Testing an integration is a POST because it makes an outbound call. It is not
 * a query: nothing should prefetch or retry it.
 */
export function useTestIntegration() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<IntegrationTestResult>(`/api/v1/integrations/${id}/test`, { method: 'POST' }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: queryKeys.integrationHealth });
      void client.invalidateQueries({ queryKey: queryKeys.integrations });
    },
  });
}

export function useSetIntegrationStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; status: 'active' | 'paused'; version: number }) =>
      apiRequest<Integration>(`/api/v1/integrations/${input.id}`, {
        method: 'PATCH',
        body: { status: input.status, version: input.version },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.integrations });
      void client.invalidateQueries({ queryKey: queryKeys.integrationHealth });
    },
  });
}

export function useCreateIntegration() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateIntegration) =>
      apiRequest<Integration>('/api/v1/integrations', { method: 'POST', body }),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.integrations }),
  });
}

/**
 * Settings and, optionally, a replacement credential. Omitting `credential`
 * leaves the stored one untouched -- the contract's write-once-write-only
 * rule (`integrations.ts`), so the form only sends it when the operator
 * actually chose to replace it.
 */
export function useUpdateIntegration() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: UpdateIntegration }) =>
      apiRequest<Integration>(`/api/v1/integrations/${input.id}`, {
        method: 'PATCH',
        body: input.body,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.integrations });
      void client.invalidateQueries({ queryKey: queryKeys.integrationHealth });
    },
  });
}

export function useDeleteIntegration() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/api/v1/integrations/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.integrations });
      void client.invalidateQueries({ queryKey: queryKeys.integrationHealth });
    },
  });
}

export function useAutomations(enabled = true) {
  return useQuery({
    queryKey: queryKeys.automations,
    queryFn: ({ signal }) =>
      apiRequest<Page<Automation>>('/api/v1/automations', { signal, query: { limit: 100 } }),
    enabled,
    ...defaultQueryOptions,
  });
}

export function useAutomationRuns(enabled = true, refetchInterval = 60_000) {
  return useQuery({
    queryKey: queryKeys.automationRuns,
    queryFn: ({ signal }) =>
      apiRequest<Page<AutomationRun>>('/api/v1/automations/runs', { signal, query: { limit: 25 } }),
    enabled,
    ...defaultQueryOptions,
    refetchInterval,
  });
}

export function useRunAutomation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ queued: true }>(`/api/v1/automations/${id}/run`, { method: 'POST' }),
    onSuccess: () => {
      // The run is queued, not finished. Refetching immediately would show the
      // previous state; the list polls, so it catches up on its own.
      void client.invalidateQueries({ queryKey: queryKeys.automations });
    },
  });
}

export function useSetAutomationEnabled() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; enabled: boolean; version: number }) =>
      apiRequest<Automation>(`/api/v1/automations/${input.id}`, {
        method: 'PATCH',
        body: { enabled: input.enabled, version: input.version },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.automations });
    },
  });
}

export function useAnnotations(filters: { acknowledged?: 'true' | 'false' } = {}) {
  return useQuery({
    queryKey: queryKeys.annotations(filters),
    queryFn: ({ signal }) =>
      apiRequest<Page<Annotation>>('/api/v1/annotations', {
        signal,
        query: { ...filters, limit: 50 },
      }),
    ...defaultQueryOptions,
    refetchInterval: 60_000,
  });
}

export function useAcknowledgeAnnotation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<Annotation>(`/api/v1/annotations/${id}/acknowledge`, { method: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['annotations'] });
    },
  });
}

/**
 * Used to resolve an owner id to a display name across the scheduling views
 * and elsewhere. `enabled` defaults to true but every scheduling view passes
 * `canAny('user.read')` explicitly, since a viewer without that permission
 * would otherwise see a silent 403 in the network tab for no visible reason.
 */
/* ------------------------------------------------------------------- map */

/**
 * The background map's configuration.
 *
 * Cached hard and never refetched on an interval: it changes when an operator
 * re-points the tile server, which is a deployment event rather than something
 * that happens while somebody is looking at a map.
 */
export function useBasemapDescriptor(projectId?: string, enabled = true) {
  const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return useQuery({
    queryKey: queryKeys.basemap(projectId),
    queryFn: ({ signal }) =>
      apiRequest<BasemapDescriptor>(`/api/v1/map/basemap/descriptor${query}`, { signal }),
    enabled,
    ...defaultQueryOptions,
    staleTime: 30 * 60_000,
  });
}

/**
 * A project's own map settings, for the `map-defaults` settings section.
 *
 * Separate from the descriptor above even though one field overlaps: the
 * descriptor is the resolved answer a map opens with, and this is the stored
 * override plus what it would fall back to -- which is what an editor needs and
 * a map has no use for.
 */
export function useProjectMapSettings(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.projectMapSettings(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<ProjectMapSettings>(`/api/v1/projects/${projectId}/map/settings`, { signal }),
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
  });
}

/**
 * The deployment's background map, for the `map-basemap` settings section.
 *
 * Not folded into the descriptor: that is the resolved answer a map opens with,
 * carrying a same-origin style URL and no upstream in it at all. This is the
 * upstream -- the URLs, the tile geometry, the key -- which only an
 * administrator configuring the background has any use for.
 */
export function useBasemapSettings() {
  return useQuery({
    queryKey: queryKeys.basemapSettings,
    queryFn: ({ signal }) =>
      apiRequest<BasemapSettings>('/api/v1/map/basemap-settings', { signal }),
    ...defaultQueryOptions,
  });
}

export function useUpdateBasemapSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateBasemapSettings) =>
      apiRequest<BasemapSettings>('/api/v1/map/basemap-settings', { method: 'PUT', body }),
    onSuccess: (saved) => {
      client.setQueryData(queryKeys.basemapSettings, saved);
      /*
       * Everything under `map`, which is broader than it looks and deliberately
       * so. The descriptor is cached for half an hour on the assumption that
       * re-pointing a tile server is a deployment event -- it still is, except
       * that it is now done from a form, so the cache has to be told, for every
       * project's descriptor rather than one. Each project's own settings
       * section reports what it inherits, and that just changed too.
       */
      void client.invalidateQueries({ queryKey: ['map'] });
    },
  });
}

export function useUpdateProjectMapSettings(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProjectMapSettings) =>
      apiRequest<ProjectMapSettings>(`/api/v1/projects/${projectId}/map/settings`, {
        method: 'PUT',
        body,
      }),
    onSuccess: (saved) => {
      client.setQueryData(queryKeys.projectMapSettings(projectId), saved);
      /*
       * The descriptor carries the resolved camera, and it is cached for half an
       * hour on the assumption that the basemap only changes when an operator
       * re-points a tile server. That assumption now has an exception, and this
       * is it -- without the invalidation the administrator saves a new default
       * view and the map they open next still uses the old one, for thirty
       * minutes, with nothing to suggest the save did not work.
       */
      void client.invalidateQueries({ queryKey: ['map', 'basemap'] });
    },
  });
}

/** Layer counts and the project footprint: what exists, not what is on screen. */
export function useMapOverview(projectId: string | undefined, refetchInterval?: number) {
  return useQuery({
    queryKey: queryKeys.mapOverview(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<MapOverview>(`/api/v1/projects/${projectId}/map/overview`, { signal }),
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
    refetchInterval,
  });
}

/**
 * The features themselves.
 *
 * Note what is *not* here: a viewport. The endpoint takes a bounding box and
 * this hook never sends one, which is deliberate for a single project's own
 * features -- a few hundred rows fetched once, then panned and zoomed with no
 * further requests, rather than a refetch on every gesture. The parameter
 * exists on the API for the day a deployment's map outgrows that, and the fix
 * is to pass it here.
 */
export function useMapFeatures(
  projectId: string | undefined,
  filters: { layer?: MapLayerKey[] } = {},
  refetchInterval?: number,
) {
  return useQuery({
    queryKey: queryKeys.mapFeatures(projectId ?? '', filters),
    queryFn: ({ signal }) =>
      apiRequest<MapFeatureCollection>(`/api/v1/projects/${projectId}/map/features`, {
        signal,
        query: { layer: filters.layer },
      }),
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
    refetchInterval,
  });
}

/** One feature in full, fetched when the reader selects it and cached per id,
 *  so clicking back and forth between two features is instant the second time. */
export function useMapFeature(projectId: string | undefined, featureId: string | null) {
  return useQuery({
    queryKey: queryKeys.mapFeature(projectId ?? '', featureId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<MapFeatureDetail>(`/api/v1/projects/${projectId}/map/features/${featureId}`, {
        signal,
      }),
    enabled: Boolean(projectId) && Boolean(featureId),
    ...defaultQueryOptions,
  });
}

/**
 * Search.
 *
 * `placeholderData` keeps the previous term's hits on screen while the next
 * request runs, so a list does not empty and refill between keystrokes -- which
 * is what makes a search feel like it is thinking rather than flickering.
 */
export function useMapSearch(projectId: string | undefined, term: string) {
  const trimmed = term.trim();

  return useQuery({
    queryKey: queryKeys.mapSearch(projectId ?? '', trimmed),
    queryFn: ({ signal }) =>
      apiRequest<MapSearchResult>(`/api/v1/projects/${projectId}/map/search`, {
        signal,
        query: { q: trimmed },
      }),
    enabled: Boolean(projectId) && trimmed !== '',
    placeholderData: (previous) => previous,
    ...defaultQueryOptions,
    staleTime: 60_000,
  });
}

/**
 * Everything about one project's map, invalidated together.
 *
 * A feature that is created, redrawn or deleted changes the collection, the
 * layer counts, the project footprint, the search index and its own detail
 * record. Those are five query keys under one prefix, and a mutation that
 * patched some of them would leave a legend counting features the map no
 * longer draws.
 *
 * The cross-reference lists go with them. A map feature is a link *candidate*
 * and a link *target*, so a new one has to appear in pickers and a deleted one
 * has to disappear from lists on items this mutation never names -- the same
 * reasoning as `invalidateLinks`, applied from the other end.
 */
function invalidateMapFeatures(client: ReturnType<typeof useQueryClient>, projectId: string): void {
  void client.invalidateQueries({ queryKey: ['map', projectId] });
  void client.invalidateQueries({ queryKey: ['links'] });
}

export function useCreateMapFeature(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMapFeature) =>
      apiRequest<MapFeatureDetail>(`/api/v1/projects/${projectId}/map/features`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateMapFeatures(client, projectId),
  });
}

export function useUpdateMapFeature(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { featureId: string; body: UpdateMapFeature }) =>
      apiRequest<MapFeatureDetail>(
        `/api/v1/projects/${projectId}/map/features/${input.featureId}`,
        { method: 'PATCH', body: input.body },
      ),
    onSuccess: () => invalidateMapFeatures(client, projectId),
  });
}

export function useDeleteMapFeature(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (featureId: string) =>
      apiRequest<void>(`/api/v1/projects/${projectId}/map/features/${featureId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidateMapFeatures(client, projectId),
  });
}

/* -------------------------------------------------------------------------- */
/* Cross-references                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Everything linked to one item, presented from that item.
 *
 * The anchor is a kind and an id rather than an entity, so the same hook serves
 * a schedule task, a map feature, and whatever the next module adds, with no
 * change here.
 */
export function useEntityLinks(anchor: EntityRef | null) {
  return useQuery({
    queryKey: queryKeys.entityLinks(anchor?.kind ?? '', anchor?.id ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<EntityLinkList>('/api/v1/links', {
        signal,
        query: { kind: anchor?.kind, id: anchor?.id },
      }),
    enabled: Boolean(anchor),
    ...defaultQueryOptions,
  });
}

/**
 * What the picker offers.
 *
 * `placeholderData` keeps the previous term's results on screen while the next
 * request runs, so the list does not empty and refill between keystrokes --
 * which is what makes a search feel like it is thinking rather than flickering.
 */
export function useLinkCandidates(
  kind: LinkEntityKind | null,
  filters: { q?: string; projectId?: string; exclude?: EntityRef } = {},
  enabled = true,
) {
  const query = {
    q: filters.q || undefined,
    projectId: filters.projectId,
    excludeKind: filters.exclude?.kind,
    excludeId: filters.exclude?.id,
  };

  return useQuery({
    queryKey: queryKeys.linkCandidates(kind ?? '', query),
    queryFn: ({ signal }) =>
      apiRequest<LinkCandidateList>('/api/v1/links/candidates', {
        signal,
        query: { ...query, kind: kind ?? undefined },
      }),
    enabled: enabled && Boolean(kind),
    placeholderData: (previous) => previous,
    ...defaultQueryOptions,
  });
}

/**
 * Invalidates every cross-reference list.
 *
 * Deliberately broad. A link has two ends, and changing it changes the list on
 * both -- but a mutation only reliably knows the end the user was standing on.
 * Invalidating the prefix costs a refetch of lists nobody is looking at, which
 * React Query does lazily anyway, and buys the guarantee that the far end is
 * never stale. Narrowing this to two keys would be an optimization that is
 * wrong whenever a derived link is involved, since those have no ids at all.
 */
function invalidateLinks(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['links'] });
}

export function useCreateEntityLink() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEntityLink) =>
      apiRequest<EntityLink>('/api/v1/links', { method: 'POST', body }),
    onSuccess: () => invalidateLinks(client),
  });
}

export function useUpdateEntityLink() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: UpdateEntityLink }) =>
      apiRequest<EntityLink>(`/api/v1/links/${input.id}`, { method: 'PATCH', body: input.body }),
    onSuccess: () => invalidateLinks(client),
  });
}

export function useDeleteEntityLink() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/api/v1/links/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateLinks(client),
  });
}

/**
 * Every account in the organization, for the pickers and the name lookups.
 *
 * Fetched to completion rather than paged, because nothing that uses it is a
 * list somebody reads: it becomes a `Map` from id to display name, and a task
 * owner whose account fell off page one rendered as a blank cell rather than as
 * a name. A picker that cannot offer somebody is a picker that quietly makes
 * them unassignable.
 *
 * Cached for five minutes and shared by every caller, so the extra pages are
 * fetched once per session rather than per screen. Organizations reach hundreds
 * of accounts, not millions; the admin Users page pages properly and is what
 * would need revisiting if that ever stopped being true.
 */
export function useUsers(enabled = true) {
  return useCompleteQuery<User>(
    queryKeys.users,
    ({ signal, cursor }) =>
      apiRequest<Page<User>>('/api/v1/users', { signal, query: { limit: 100, cursor } }),
    { enabled, staleTime: 5 * 60_000 },
  );
}

/* -------------------------------------------------------------------------- */
/* User and role administration                                                */
/* -------------------------------------------------------------------------- */

/** The filtered, paginated list behind the admin Users page -- distinct from
 * `useUsers`, which every picker in the product shares unfiltered. */
export function useUsersPage(
  filters: { status?: string; search?: string; partyId?: string; limit?: number } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.usersPage(filters),
    queryFn: ({ signal }) =>
      apiRequest<Page<User>>('/api/v1/users', { signal, query: { ...filters } }),
    enabled,
    ...defaultQueryOptions,
  });
}

function invalidateUsers(client: ReturnType<typeof useQueryClient>) {
  // 'users' is a shared prefix: it also invalidates the unfiltered picker list.
  void client.invalidateQueries({ queryKey: queryKeys.users });
}

/** Admin-initiated invitation (section 12.2): creates the account ahead of
 * the person's first sign-in. */
export function useInviteUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteUser) => apiRequest<User>('/api/v1/users', { method: 'POST', body }),
    onSuccess: () => invalidateUsers(client),
  });
}

/** Profile and status editing -- never roles, which stay behind `useAssignRoles`
 * and its own, stricter permission. */
export function useUpdateUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: UpdateUser }) =>
      apiRequest<User>(`/api/v1/users/${input.id}`, { method: 'PATCH', body: input.body }),
    onSuccess: () => invalidateUsers(client),
  });
}

/** Replaces a user's role assignment; the server revokes their sessions. */
export function useAssignRoles() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; roleIds: string[] }) =>
      apiRequest<User>(`/api/v1/users/${input.id}/roles`, {
        method: 'POST',
        body: { roleIds: input.roleIds },
      }),
    onSuccess: () => invalidateUsers(client),
  });
}

/**
 * Moves an account to another organization (docs/organizations-design.md
 * section 3.4). The server revokes their sessions; the organization register's
 * member counts move with it.
 */
export function useChangeMembership() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: ChangeMembership }) =>
      apiRequest<User>(`/api/v1/users/${input.id}/organization`, {
        method: 'PUT',
        body: input.body,
      }),
    onSuccess: () => {
      invalidateUsers(client);
      void client.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useRoles(enabled = true) {
  return useQuery({
    queryKey: queryKeys.roles,
    queryFn: ({ signal }) => apiRequest<Role[]>('/api/v1/roles', { signal }),
    enabled,
    ...defaultQueryOptions,
  });
}

/** The permission catalog, for the checklist a role's permissions are built
 * from. Static for the lifetime of a deployment. */
export function usePermissionCatalog(enabled = true) {
  return useQuery({
    queryKey: queryKeys.permissionCatalog,
    queryFn: ({ signal }) =>
      apiRequest<{ permissions: Permission[] }>('/api/v1/permissions', { signal }),
    enabled,
    staleTime: Infinity,
    retry: shouldRetry,
  });
}

export function useCreateRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRole) => apiRequest<Role>('/api/v1/roles', { method: 'POST', body }),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.roles }),
  });
}

export function useUpdateRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: UpdateRole }) =>
      apiRequest<Role>(`/api/v1/roles/${input.id}`, { method: 'PATCH', body: input.body }),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.roles }),
  });
}

export function useDeleteRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/api/v1/roles/${id}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.roles }),
  });
}

/** Self-service editing of the caller's own display name and locale (section
 * 13.3): no permission required, and the schema behind it has no field for
 * status or roles. Invalidates the session context so the new values show up
 * everywhere they are read from it, immediately. */
export function useUpdateOwnProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateOwnProfile) =>
      apiRequest<{ displayName: string; locale: string }>('/api/v1/me', {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.authStatus }),
  });
}

/* -------------------------------------------------------------------------- */
/* Scheduling                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Every scheduling query and mutation lives under one project (the "single
 * project, not a portfolio" scoping described in docs/decisions.md), so every
 * hook here takes `projectId` first. `enabled` follows `Boolean(projectId)`
 * automatically -- a caller waiting on the user to pick a project should not
 * also have to remember to gate the query.
 */

/**
 * Every task in the project, fetched to completion.
 *
 * Not a "load more" list, and the difference is the whole point: the Gantt lays
 * out a hierarchy, the board groups by status, and the dependency arrows join
 * rows to other rows. All three are wrong -- not merely partial -- if a task is
 * missing. A parent whose children are on page two draws as a leaf; an arrow
 * whose predecessor never arrived draws nowhere.
 *
 * It used to ask for `limit: 200`, the largest page the endpoint allows, and
 * take whatever came back. That made two hundred a silent ceiling on how big a
 * project this product could draw. Edit mode never had it -- the change
 * session's projection returns the whole project -- so a schedule could also
 * gain tasks on entering edit mode and lose them again on leaving.
 *
 * Pages arrive in sequence and `items` grows as they land, so a large project
 * fills in rather than blocking; `isComplete` is what a caller checks before
 * drawing something that must not be half-right.
 */
export function useScheduleTasks(
  projectId: string | undefined,
  filters: {
    ownerUserId?: string;
    workflowStatus?: string;
    hasWarnings?: boolean;
    search?: string;
  } = {},
  refetchInterval?: number,
) {
  return useCompleteQuery<ScheduleTask>(
    queryKeys.scheduleTasks(projectId ?? '', filters),
    ({ signal, cursor }) =>
      apiRequest<Page<ScheduleTask>>(`/api/v1/projects/${projectId}/schedule/tasks`, {
        signal,
        query: { ...filters, limit: 200, cursor },
      }),
    { enabled: Boolean(projectId), ...(refetchInterval ? { refetchInterval } : {}) },
  );
}

/**
 * The four numbers the schedule dashboard tile shows, counted by the API.
 *
 * Replaces the tile's old habit of calling `useScheduleTasks` and counting in
 * the browser: a page of two hundred hydrated task rows, on a two-minute poll,
 * to render four integers -- and four wrong integers on any project with more
 * tasks than the page held.
 */
export function useScheduleSummary(projectId: string | undefined, refetchInterval?: number) {
  return useQuery({
    queryKey: queryKeys.scheduleSummary(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<ScheduleSummary>(`/api/v1/projects/${projectId}/schedule/tasks/summary`, {
        signal,
      }),
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
    refetchInterval,
  });
}

export function useScheduleDependencies(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.scheduleDependencies(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<ScheduleDependency[]>(`/api/v1/projects/${projectId}/schedule/dependencies`, {
        signal,
      }),
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
  });
}

export function useScheduleCalendar(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.scheduleCalendar(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<ScheduleCalendar>(`/api/v1/projects/${projectId}/schedule/calendar`, { signal }),
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
  });
}

/** Invalidates everything a schedule mutation could have changed: every task's
 * calculated dates can shift from a single edit (section 6.3's propagation),
 * so a targeted cache update would risk showing a stale downstream date. */
function invalidateSchedule(client: ReturnType<typeof useQueryClient>, projectId: string): void {
  void client.invalidateQueries({ queryKey: ['schedule', projectId] });
  /* A task is something other items are cross-referenced to, and the picker's
   * candidate lists are keyed under `links` rather than under this project. A
   * task created from a link picker has to appear in the list that offered to
   * create it -- see the entity creator registry. */
  void client.invalidateQueries({ queryKey: ['links'] });
}

export function useCreateScheduleTask(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateScheduleTask) =>
      apiRequest<ScheduleTask>(`/api/v1/projects/${projectId}/schedule/tasks`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateSchedule(client, projectId),
  });
}

export function useUpdateScheduleTask(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: UpdateScheduleTask }) =>
      apiRequest<ScheduleTask>(`/api/v1/projects/${projectId}/schedule/tasks/${input.id}`, {
        method: 'PATCH',
        body: input.body,
      }),
    onSuccess: () => invalidateSchedule(client, projectId),
  });
}

/**
 * Applies one task's workflow status to every cached list holding that task.
 *
 * Two shapes carry tasks under `['schedule', projectId]`: the published page
 * (`{ items }`) and a change session's projection (`{ tasks }`). Which of them
 * the board is reading depends on whether edit mode is on, so both are
 * patched rather than the caller having to know. Anything else under that key
 * -- dependencies, the calendar, the session itself -- has neither field and
 * passes through untouched.
 */
function patchCachedTask(
  client: ReturnType<typeof useQueryClient>,
  projectId: string,
  taskId: string,
  update: Partial<ScheduleTask>,
): void {
  const apply = (tasks: readonly ScheduleTask[]) =>
    tasks.map((task) => (task.id === taskId ? { ...task, ...update } : task));

  client.setQueriesData({ queryKey: ['schedule', projectId] }, (data: unknown): unknown => {
    if (data === null || typeof data !== 'object') return data;
    if ('items' in data && Array.isArray(data.items)) {
      return { ...data, items: apply(data.items as ScheduleTask[]) };
    }
    if ('tasks' in data && Array.isArray(data.tasks)) {
      return { ...data, tasks: apply(data.tasks as ScheduleTask[]) };
    }
    return data;
  });
}

/**
 * Status is the one schedule mutation that updates the cache optimistically.
 *
 * Every other one invalidates and waits, which is right for them: an edit to a
 * date propagates, and a guess at the result would be a guess at the whole
 * engine's output. A status change propagates to nothing at all -- it is one
 * enum on one row -- so the answer is known before the request is sent.
 *
 * The board is the reason it matters. Dropping a card ends its drag in the
 * same commit, so without this the card lands back in the column it came from
 * and only moves when the refetch arrives: a visible flash back to where it
 * started. The optimistic write happens *before* this function's first
 * `await`, which keeps it inside the pointer-up handler's own React batch --
 * the drop and the move are then one commit, and there is no frame in between
 * for the old position to be painted in.
 */
export function useUpdateScheduleTaskStatus(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: UpdateScheduleTaskStatus }) =>
      apiRequest<ScheduleTask>(`/api/v1/projects/${projectId}/schedule/tasks/${input.id}/status`, {
        method: 'PATCH',
        body: input.body,
      }),
    onMutate: async (input) => {
      const snapshot = client.getQueriesData({ queryKey: ['schedule', projectId] });
      patchCachedTask(client, projectId, input.id, { workflowStatus: input.body.workflowStatus });
      // After the write, not before: an in-flight refetch must not land on top
      // of the optimistic value, but awaiting first would push that value out
      // of this event's batch and reintroduce the flash it exists to prevent.
      await client.cancelQueries({ queryKey: ['schedule', projectId] });
      return { snapshot };
    },
    onError: (_error, _input, context) => {
      // The card returns to the column it came from, which is the truth again
      // -- the page's own error banner says why it did not stay where it was put.
      for (const [key, data] of context?.snapshot ?? []) client.setQueryData(key, data);
    },
    // The server's own copy carries the new `version`, so a second drag of the
    // same card does not race the refetch and lose on a stale one.
    onSuccess: (updated) => patchCachedTask(client, projectId, updated.id, updated),
    onSettled: () => invalidateSchedule(client, projectId),
  });
}

/**
 * Progress: reporting it, reading its history, and choosing how it is measured.
 *
 * Three hooks rather than one because they are three different acts by
 * different people with different rights (docs/schedule-progress-design.md
 * section 9): whoever does the work reports against it, and only a planner
 * decides what the project's completion number is made of.
 *
 * None of them writes optimistically, unlike the status hook above. A reported
 * percentage propagates -- up every rollup, and into every ancestor's variance
 * against the plan -- so a guess at the result would be a guess at the whole
 * tree, which is the same reason the task mutations do not guess either.
 */
export function useReportScheduleProgress(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: CreateScheduleProgressReport }) =>
      apiRequest<void>(`/api/v1/projects/${projectId}/schedule/tasks/${input.id}/progress`, {
        method: 'POST',
        body: input.body,
      }),
    onSuccess: (_result, input) => {
      void client.invalidateQueries({ queryKey: ['schedule', projectId, 'progress', input.id] });
      return invalidateSchedule(client, projectId);
    },
  });
}

/** The weekly update: many rows, one occasion, one save (design 10.3). */
export function useReportScheduleProgressBatch(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: BatchScheduleProgressReport) =>
      apiRequest<void>(`/api/v1/projects/${projectId}/schedule/tasks/progress`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['schedule', projectId, 'progress'] });
      return invalidateSchedule(client, projectId);
    },
  });
}

/** Earned value: what the spend bought (docs/schedule-progress-design.md 11). */
export function useCostEarnedValue(projectId: string, enabled = true) {
  return useQuery({
    queryKey: ['costs', projectId, 'earned-value'],
    enabled,
    queryFn: () => apiRequest<CostEarnedValue>(`/api/v1/projects/${projectId}/costs/earned-value`),
  });
}

/** The project's completion over time, for the curve. */
export function useScheduleProgressCurve(projectId: string, enabled = true) {
  return useQuery({
    queryKey: ['schedule', projectId, 'progress-curve'],
    enabled,
    queryFn: () =>
      apiRequest<ScheduleProgressCurve>(
        `/api/v1/projects/${projectId}/schedule/tasks/progress-curve`,
      ),
  });
}

export function useScheduleProgressHistory(projectId: string, taskId: string | null) {
  return useQuery({
    queryKey: ['schedule', projectId, 'progress', taskId],
    enabled: taskId !== null,
    queryFn: () =>
      apiRequest<ScheduleProgressReport[]>(
        `/api/v1/projects/${projectId}/schedule/tasks/${taskId}/progress`,
      ),
  });
}

/**
 * Changing how a row is measured, which bumps that row's `version`.
 *
 * The updated task is returned rather than discarded: these controls render
 * inside the edit dialog, whose own `baseVersion` was fixed when it opened, so
 * without adopting the new one the dialog's next save is refused against a
 * change the same person just made -- reported as "somebody else modified
 * this", with nobody else involved.
 */
export function useUpdateScheduleProgressSettings(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: UpdateScheduleProgressSettings }) =>
      apiRequest<ScheduleTask>(
        `/api/v1/projects/${projectId}/schedule/tasks/${input.id}/progress-settings`,
        { method: 'PATCH', body: input.body },
      ),
    onSuccess: () => invalidateSchedule(client, projectId),
  });
}

/**
 * The project's budget lines and which task each belongs to.
 *
 * Project-scoped and cached once: the task panel filters it per row, so a
 * planner working down a work breakdown fetches this catalogue once rather
 * than once per task they open.
 */
export function useScheduleCostLinks(projectId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.scheduleCostLinks(projectId),
    enabled,
    queryFn: ({ signal }) =>
      apiRequest<ScheduleCostLinks>(`/api/v1/projects/${projectId}/schedule/cost-links`, {
        signal,
      }),
  });
}

/**
 * Attach a budget line to a task, move it, or detach it.
 *
 * Invalidates the schedule as well as this catalogue, because linking is a
 * write to the schedule in every way that matters to the reader: it moves the
 * task's stored progress, the money shown beside it, and the roll-up of every
 * summary above it. The cost pages are invalidated too -- a line's task is
 * shown there as well, and the budget's own screens must not keep showing the
 * previous answer.
 */
export function useSetScheduleCostLink(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { budgetLineId: string; scheduleTaskId: string | null }) =>
      apiRequest<void>(`/api/v1/projects/${projectId}/schedule/cost-links/${input.budgetLineId}`, {
        method: 'PUT',
        body: { scheduleTaskId: input.scheduleTaskId },
      }),
    onSuccess: () => {
      invalidateSchedule(client, projectId);
      void client.invalidateQueries({ queryKey: ['costs', projectId] });
    },
  });
}

export function usePublishScheduleTask(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: PublishScheduleTask }) =>
      apiRequest<ScheduleTask>(`/api/v1/projects/${projectId}/schedule/tasks/${input.id}/publish`, {
        method: 'POST',
        body: input.body,
      }),
    onSuccess: () => invalidateSchedule(client, projectId),
  });
}

export function useDeleteScheduleTask(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/api/v1/projects/${projectId}/schedule/tasks/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateSchedule(client, projectId),
  });
}

export function useCreateScheduleDependency(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateScheduleDependency) =>
      apiRequest<ScheduleDependency>(`/api/v1/projects/${projectId}/schedule/dependencies`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateSchedule(client, projectId),
  });
}

export function useDeleteScheduleDependency(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/api/v1/projects/${projectId}/schedule/dependencies/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidateSchedule(client, projectId),
  });
}

export function useUpdateScheduleCalendar(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateScheduleCalendar) =>
      apiRequest<ScheduleCalendar>(`/api/v1/projects/${projectId}/schedule/calendar`, {
        method: 'PUT',
        body,
      }),
    onSuccess: () => invalidateSchedule(client, projectId),
  });
}

/* -------------------------------------------------------------------------- */
/* Rescheduling sessions and the schedule inbox                                */
/* -------------------------------------------------------------------------- */

/**
 * The caller's open change session for a project, or null.
 *
 * The one query the whole editing experience hangs off: while it returns a
 * session, the page shows proposed dates and edits are staged; when it returns
 * null, edits apply immediately. Kept deliberately cheap so every view can ask.
 */
export function useScheduleSession(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.scheduleSession(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<{ session: ScheduleChangeSession | null }>(
        `/api/v1/projects/${projectId}/schedule/sessions/current`,
        { signal },
      ).then((response) => response.session),
    enabled: Boolean(projectId) && enabled,
    ...defaultQueryOptions,
  });
}

/**
 * The projected schedule and its impact. This is what the views render while a
 * session is open, so that "what it would look like" and "who that affects"
 * can never disagree -- they arrive in the same payload.
 */
export function useScheduleSessionPreview(
  projectId: string | undefined,
  sessionId: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.scheduleSessionPreview(projectId ?? '', sessionId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<ScheduleSessionPreview>(
        `/api/v1/projects/${projectId}/schedule/sessions/${sessionId}/preview`,
        { signal },
      ),
    enabled: Boolean(projectId) && Boolean(sessionId),
    ...defaultQueryOptions,
  });
}

export function useOpenScheduleSession(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<ScheduleChangeSession>(`/api/v1/projects/${projectId}/schedule/sessions`, {
        method: 'POST',
      }),
    onSuccess: () => invalidateSchedule(client, projectId),
  });
}

/**
 * Stages one task's proposal.
 *
 * The session id is a call argument rather than a hook argument because an
 * edit can create the session it stages into: a save that turns out to need
 * acceptance opens one and stages the same patch, and at the moment the hook
 * was built there was no session to bind to.
 */
export function useStageScheduleChange(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; taskId: string; body: ScheduleTaskPatch }) =>
      apiRequest<ScheduleChangeSession>(
        `/api/v1/projects/${projectId}/schedule/sessions/${input.sessionId}/changes/${input.taskId}`,
        { method: 'PUT', body: input.body },
      ),
    onSuccess: () => invalidateSchedule(client, projectId),
  });
}

export function useDiscardScheduleChange(projectId: string, sessionId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) =>
      apiRequest<ScheduleChangeSession>(
        `/api/v1/projects/${projectId}/schedule/sessions/${sessionId}/changes/${taskId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => invalidateSchedule(client, projectId),
  });
}

export function useSubmitScheduleSession(projectId: string, sessionId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitScheduleSession) =>
      apiRequest<ScheduleSessionPreview>(
        `/api/v1/projects/${projectId}/schedule/sessions/${sessionId}/submit`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      invalidateSchedule(client, projectId);
      invalidateScheduleInbox(client);
    },
  });
}

export function useCancelScheduleSession(projectId: string, sessionId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<ScheduleChangeSession>(
        `/api/v1/projects/${projectId}/schedule/sessions/${sessionId}/cancel`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      invalidateSchedule(client, projectId);
      invalidateScheduleInbox(client);
    },
  });
}

/** An impacted owner's answer to a request for acceptance. */
export function useDecideScheduleSession(projectId: string, sessionId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: DecideScheduleSession) =>
      apiRequest<ScheduleChangeSession>(
        `/api/v1/projects/${projectId}/schedule/sessions/${sessionId}/decision`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      invalidateSchedule(client, projectId);
      invalidateScheduleInbox(client);
    },
  });
}

function invalidateScheduleInbox(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['schedule-inbox'] });
}

export function useScheduleNotifications(enabled = true) {
  return useQuery({
    queryKey: queryKeys.scheduleNotifications,
    queryFn: ({ signal }) =>
      apiRequest<Page<ScheduleNotification>>('/api/v1/schedule/notifications', {
        signal,
        query: { limit: 50 },
      }),
    enabled,
    ...defaultQueryOptions,
  });
}

/**
 * Two counts for the navigation badge.
 *
 * Polled, because a schedule change arrives from somebody else's action rather
 * than from anything this browser did. One minute is slow enough to be
 * invisible in load terms and fast enough that "you are blocking a colleague"
 * does not sit unseen for an afternoon.
 */
export function useScheduleInboxSummary(enabled = true) {
  return useQuery({
    queryKey: queryKeys.scheduleInboxSummary,
    queryFn: ({ signal }) =>
      apiRequest<ScheduleInboxSummary>('/api/v1/schedule/notifications/summary', { signal }),
    enabled,
    ...defaultQueryOptions,
    refetchInterval: 60_000,
  });
}

export function useMarkScheduleNotificationRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<ScheduleNotification>(`/api/v1/schedule/notifications/${id}/read`, {
        method: 'POST',
      }),
    onSuccess: () => invalidateScheduleInbox(client),
  });
}

export function useMarkAllScheduleNotificationsRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<{ updated: number }>('/api/v1/schedule/notifications/read-all', {
        method: 'POST',
      }),
    onSuccess: () => invalidateScheduleInbox(client),
  });
}

export function useCreateView() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateView) => apiRequest<View>('/api/v1/views', { method: 'POST', body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.views });
    },
  });
}

/**
 * Removing a dashboard.
 *
 * Takes the slug as well as the id because the two caches are keyed
 * differently: the list is a single entry, and the dashboard itself is keyed by
 * slug. Invalidating only the list would leave the deleted dashboard's own
 * entry behind, so navigating back to its address -- from a bookmark, or the
 * browser's back button -- would serve it from cache and render a dashboard
 * that no longer exists until the refetch caught up.
 *
 * `removeQueries` rather than `invalidateQueries` for that entry, because there
 * is nothing to revalidate: the correct answer is now a 404, and asking for one
 * only to render an error is slower than dropping it.
 */
export function useDeleteView() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; slug: string }) =>
      apiRequest<void>(`/api/v1/views/${id}`, { method: 'DELETE' }),
    onSuccess: (_deleted, { slug }) => {
      client.removeQueries({ queryKey: queryKeys.view(slug) });
      void client.invalidateQueries({ queryKey: queryKeys.views });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Cost monitoring                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The overview is one request for the whole page, matching the batched endpoint
 * (cost monitoring requirements FR-039). Four separate queries would render the
 * KPI row against a different snapshot than the chart under it, and a page that
 * contradicts itself is worse than a page that loads a moment later.
 */
export function useCostOverview(
  projectId: string | undefined,
  filters: { accountId?: string; parentAccountId?: string; from?: string; to?: string } = {},
  refetchInterval?: number,
) {
  return useQuery({
    queryKey: queryKeys.costOverview(projectId ?? '', filters),
    queryFn: ({ signal }) =>
      apiRequest<CostOverview>(`/api/v1/projects/${projectId}/costs/overview`, {
        signal,
        query: filters,
      }),
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
    refetchInterval,
  });
}

/**
 * The two narrow cost reads the dashboard tiles use.
 *
 * Deliberately not `useCostOverview`, which is one request for the whole Costs
 * page: it computes the account breakdown, the waterfall and this reader's
 * write authority as well, none of which a tile draws. Section 9.3 is explicit
 * that a component should not receive whole backend entities when a view model
 * will do, and the cost of ignoring it here is paid by every viewer of a shared
 * dashboard on every refresh cycle.
 *
 * Both endpoints already existed for the page; only these hooks are new.
 *
 * They also do not share the overview's cache entry, which is the point: a tile
 * and the page it links to poll on different cadences, and folding them
 * together would make the dashboard's five-minute interval govern a page the
 * reader is actively working in.
 */
export function useCostSummary(
  projectId: string | undefined,
  enabled = true,
  refetchInterval?: number,
) {
  return useQuery({
    queryKey: queryKeys.costSummary(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<CostSummary>(`/api/v1/projects/${projectId}/costs/summary`, { signal }),
    enabled: enabled && Boolean(projectId),
    ...defaultQueryOptions,
    refetchInterval,
  });
}

export function useCostTimeseries(
  projectId: string | undefined,
  filters: { from?: string; to?: string } = {},
  enabled = true,
  refetchInterval?: number,
) {
  return useQuery({
    queryKey: queryKeys.costTimeseries(projectId ?? '', filters),
    queryFn: ({ signal }) =>
      apiRequest<CostTimeseries>(`/api/v1/projects/${projectId}/costs/timeseries`, {
        signal,
        query: filters,
      }),
    enabled: enabled && Boolean(projectId),
    ...defaultQueryOptions,
    refetchInterval,
  });
}

export function useCostAccounts(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.costAccounts(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<CostAccount[]>(`/api/v1/projects/${projectId}/costs/accounts`, { signal }),
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
  });
}

/**
 * The entry list needs `cost.read_detail`. It is queried only when the caller
 * says the session holds it, so a reader without the permission sees the
 * explicit "not permitted" state rather than a request that 403s on every
 * render (FR-042).
 */
export function useCostEntries(
  projectId: string | undefined,
  filters: {
    accountId?: string;
    ledger?: string;
    from?: string;
    to?: string;
    includeVoided?: string;
  } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.costEntries(projectId ?? '', filters),
    queryFn: ({ signal }) =>
      apiRequest<Page<CostEntry>>(`/api/v1/projects/${projectId}/costs/entries`, {
        signal,
        query: { ...filters, limit: 50 },
      }),
    enabled: Boolean(projectId) && enabled,
    ...defaultQueryOptions,
  });
}

/**
 * Suggestions for `SuggestInput` on the document-group field. `q` is already
 * debounced by the caller, so every value change here is worth a request.
 */
export function useDocumentGroupSuggestions(
  projectId: string | undefined,
  q: string,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.documentGroupSuggestions(projectId ?? '', q),
    queryFn: ({ signal }) =>
      apiRequest<string[]>(`/api/v1/projects/${projectId}/costs/entries/document-groups`, {
        signal,
        query: { q: q || undefined },
      }),
    enabled: Boolean(projectId) && enabled,
    ...defaultQueryOptions,
  });
}

export function useCostBudgetVersions(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.costBudgetVersions(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<CostBudgetVersion[]>(`/api/v1/projects/${projectId}/costs/budget-versions`, {
        signal,
      }),
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
  });
}

export function useCostBudgetLines(projectId: string | undefined, versionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.costBudgetLines(projectId ?? '', versionId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<CostBudgetLine[]>(
        `/api/v1/projects/${projectId}/costs/budget-versions/${versionId}/lines`,
        { signal },
      ),
    enabled: Boolean(projectId) && Boolean(versionId),
    ...defaultQueryOptions,
  });
}

function invalidateCosts(client: ReturnType<typeof useQueryClient>, projectId: string): void {
  void client.invalidateQueries({ queryKey: ['costs', projectId] });
}

/**
 * A new cost account.
 *
 * The links prefix goes with the cost tree for the same reason a map feature's
 * does: a cost account is something other items are cross-referenced to, so a
 * new one has to show up in a picker that is keyed under `links`, not `costs`.
 */
export function useCreateCostAccount(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCostAccount) =>
      apiRequest<CostAccount>(`/api/v1/projects/${projectId}/costs/accounts`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      invalidateCosts(client, projectId);
      void client.invalidateQueries({ queryKey: ['links'] });
    },
  });
}

export function useCreateCostEntry(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCostEntry) =>
      apiRequest<CostEntry>(`/api/v1/projects/${projectId}/costs/entries`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useVoidCostEntry(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: VoidCostEntry & { id: string }) =>
      apiRequest<CostEntry>(`/api/v1/projects/${projectId}/costs/entries/${id}/void`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useImportCosts(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CostImport) =>
      apiRequest<CostImportResult>(`/api/v1/projects/${projectId}/costs/import`, {
        method: 'POST',
        body,
      }),
    // A dry run changes nothing, so only a committed import invalidates.
    onSuccess: (result) => {
      if (result.committed) invalidateCosts(client, projectId);
    },
  });
}

export function useCostForecastVersions(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.costForecastVersions(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<CostForecastVersion[]>(`/api/v1/projects/${projectId}/costs/forecast-versions`, {
        signal,
      }),
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
  });
}

export function useCostForecastLines(projectId: string | undefined, versionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.costForecastLines(projectId ?? '', versionId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<CostForecastLine[]>(
        `/api/v1/projects/${projectId}/costs/forecast-versions/${versionId}/lines`,
        { signal },
      ),
    enabled: Boolean(projectId) && Boolean(versionId),
    ...defaultQueryOptions,
  });
}

export function useCostSources(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.costSources(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<CostSource[]>(`/api/v1/projects/${projectId}/costs/sources`, { signal }),
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
  });
}

export function useCostSettings(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.costSettings(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<CostSettings>(`/api/v1/projects/${projectId}/costs/settings`, { signal }),
    enabled: Boolean(projectId),
    ...defaultQueryOptions,
  });
}

export function useUpdateCostAccount(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCostAccount }) =>
      apiRequest<CostAccount>(`/api/v1/projects/${projectId}/costs/accounts/${id}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      invalidateCosts(client, projectId);
      void client.invalidateQueries({ queryKey: ['links'] });
    },
  });
}

/**
 * Re-parenting an account rewrites the materialized path of its whole subtree,
 * so every cost query is stale afterwards -- not only the account list.
 */
export function useMoveCostAccount(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MoveCostAccount }) =>
      apiRequest<CostAccount>(`/api/v1/projects/${projectId}/costs/accounts/${id}/move`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useArchiveCostAccount(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<CostAccount>(`/api/v1/projects/${projectId}/costs/accounts/${id}/archive`, {
        method: 'POST',
      }),
    onSuccess: () => {
      invalidateCosts(client, projectId);
      void client.invalidateQueries({ queryKey: ['links'] });
    },
  });
}

export function useUpdateCostEntry(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCostEntry }) =>
      apiRequest<CostEntry>(`/api/v1/projects/${projectId}/costs/entries/${id}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useCreateCostBudgetVersion(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCostBudgetVersion) =>
      apiRequest<CostBudgetVersion>(`/api/v1/projects/${projectId}/costs/budget-versions`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useApproveCostBudgetVersion(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ApproveCostBudgetVersion & { id: string }) =>
      apiRequest<CostBudgetVersion>(
        `/api/v1/projects/${projectId}/costs/budget-versions/${id}/approve`,
        { method: 'POST', body },
      ),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useDeleteCostBudgetVersion(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/api/v1/projects/${projectId}/costs/budget-versions/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useCreateCostBudgetLine(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, body }: { versionId: string; body: CreateCostBudgetLine }) =>
      apiRequest<CostBudgetLine>(
        `/api/v1/projects/${projectId}/costs/budget-versions/${versionId}/lines`,
        { method: 'POST', body },
      ),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useUpdateCostBudgetLine(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      versionId,
      id,
      body,
    }: {
      versionId: string;
      id: string;
      body: UpdateCostBudgetLine;
    }) =>
      apiRequest<CostBudgetLine>(
        `/api/v1/projects/${projectId}/costs/budget-versions/${versionId}/lines/${id}`,
        { method: 'PATCH', body },
      ),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useDeleteCostBudgetLine(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, id }: { versionId: string; id: string }) =>
      apiRequest<void>(
        `/api/v1/projects/${projectId}/costs/budget-versions/${versionId}/lines/${id}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useCreateCostForecastVersion(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCostForecastVersion) =>
      apiRequest<CostForecastVersion>(`/api/v1/projects/${projectId}/costs/forecast-versions`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useReplaceCostForecastLines(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReplaceCostForecastLines }) =>
      apiRequest<CostForecastVersion>(
        `/api/v1/projects/${projectId}/costs/forecast-versions/${id}/lines`,
        { method: 'PUT', body },
      ),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useIssueCostForecastVersion(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: IssueCostForecastVersion & { id: string }) =>
      apiRequest<CostForecastVersion>(
        `/api/v1/projects/${projectId}/costs/forecast-versions/${id}/issue`,
        { method: 'POST', body },
      ),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useDeleteCostForecastVersion(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/api/v1/projects/${projectId}/costs/forecast-versions/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useCreateCostSource(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCostSource) =>
      apiRequest<CostSource>(`/api/v1/projects/${projectId}/costs/sources`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useUpdateCostSource(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCostSource }) =>
      apiRequest<CostSource>(`/api/v1/projects/${projectId}/costs/sources/${id}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

/**
 * "Sync now" -- queues an out-of-schedule `cost.source_sync` run rather than
 * pulling the provider inline (section 5.4). `queued: true` is the whole
 * response; the actual data shows up once the worker finishes, which is what
 * invalidating the cost queries a beat later is for.
 */
export function useSyncCostSource(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ queued: true }>(`/api/v1/projects/${projectId}/costs/sources/${id}/sync`, {
        method: 'POST',
      }),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

/**
 * A claim changes who may write which accounts, so it invalidates everything:
 * the account list carries provenance, the overview carries the verdicts, and
 * every entry row carries the actions those verdicts produce.
 */
export function useAddCostSourceClaim(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CreateCostSourceClaim }) =>
      apiRequest<CostSource>(`/api/v1/projects/${projectId}/costs/sources/${id}/claims`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useRemoveCostSourceClaim(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, claimId }: { id: string; claimId: string }) =>
      apiRequest<CostSource>(
        `/api/v1/projects/${projectId}/costs/sources/${id}/claims/${claimId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => invalidateCosts(client, projectId),
  });
}

export function useUpdateCostSettings(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateCostSettings) =>
      apiRequest<CostSettings>(`/api/v1/projects/${projectId}/costs/settings`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      invalidateCosts(client, projectId);
      // The reporting currency is a project field, so the project query holds a
      // copy of what just changed.
      void client.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* The provider console                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The deployment record, and the checklist derived from it.
 *
 * Every mutation below invalidates the whole `['platform']` prefix rather than
 * patching the cache, because these edits reach further than their own
 * response: switching a module off changes what the session context says the
 * caller may do, and re-theming changes what the shell looks like. The session
 * query is invalidated with them so the shell re-reads both -- which is what
 * makes the navigation and the brand update without a page reload.
 */
export function useDeployment(enabled = true) {
  return useQuery({
    queryKey: queryKeys.deployment,
    queryFn: ({ signal }) => apiRequest<Deployment>('/api/v1/platform/deployment', { signal }),
    enabled,
    ...defaultQueryOptions,
  });
}

export function useDeploymentReadiness(enabled = true) {
  return useQuery({
    queryKey: queryKeys.deploymentReadiness,
    queryFn: ({ signal }) =>
      apiRequest<DeploymentReadiness>('/api/v1/platform/deployment/readiness', { signal }),
    enabled,
    ...defaultQueryOptions,
  });
}

function useDeploymentMutation<TBody>(path: string, method: 'PATCH' | 'PUT') {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) => apiRequest<Deployment>(path, { method, body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['platform'] });
      // The shell reads permissions, theme and stage from the session context;
      // all three can have just changed.
      void client.invalidateQueries({ queryKey: queryKeys.authStatus });
      // Module selection decides which tiles the picker offers.
      void client.invalidateQueries({ queryKey: queryKeys.catalog });
    },
  });
}

export function useUpdateDeploymentIdentity() {
  return useDeploymentMutation<UpdateDeploymentIdentity>('/api/v1/platform/deployment', 'PATCH');
}

export function useUpdateDeploymentTheme() {
  return useDeploymentMutation<UpdateDeploymentTheme>('/api/v1/platform/deployment/theme', 'PUT');
}

export function useUpdateDeploymentModules() {
  return useDeploymentMutation<UpdateDeploymentModules>(
    '/api/v1/platform/deployment/modules',
    'PUT',
  );
}

export function useSetDeploymentStage() {
  return useDeploymentMutation<UpdateDeploymentStage>('/api/v1/platform/deployment/stage', 'PUT');
}

/* -------------------------------------------------------------------------- */
/* Access control: who may reach this deployment                              */
/* -------------------------------------------------------------------------- */

export function useAllowedEmailDomains(enabled = true) {
  return useQuery({
    queryKey: queryKeys.allowedEmailDomains,
    queryFn: ({ signal }) =>
      apiRequest<AllowedEmailDomain[]>('/api/v1/platform/access-control/email-domains', { signal }),
    enabled,
    ...defaultQueryOptions,
  });
}

export function useCreateAllowedEmailDomain() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAllowedEmailDomain) =>
      apiRequest<AllowedEmailDomain>('/api/v1/platform/access-control/email-domains', {
        method: 'POST',
        body,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.allowedEmailDomains }),
  });
}

export function useDeleteAllowedEmailDomain() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/api/v1/platform/access-control/email-domains/${id}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.allowedEmailDomains }),
  });
}

export function useAllowedTenantIds(enabled = true) {
  return useQuery({
    queryKey: queryKeys.allowedTenantIds,
    queryFn: ({ signal }) =>
      apiRequest<AllowedTenantIdsResponse>('/api/v1/platform/access-control/tenant-ids', {
        signal,
      }),
    enabled,
    ...defaultQueryOptions,
  });
}

export function useCreateAllowedTenantId() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAllowedTenantId) =>
      apiRequest<AllowedTenantId>('/api/v1/platform/access-control/tenant-ids', {
        method: 'POST',
        body,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.allowedTenantIds }),
  });
}

export function useDeleteAllowedTenantId() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/api/v1/platform/access-control/tenant-ids/${id}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.allowedTenantIds }),
  });
}

/* -------------------------------------------------------------------------- */
/* Platform administrators                                                     */
/* -------------------------------------------------------------------------- */

export function usePlatformAdmins(enabled = true) {
  return useQuery({
    queryKey: queryKeys.platformAdmins,
    queryFn: ({ signal }) => apiRequest<PlatformAdmin[]>('/api/v1/platform/admins', { signal }),
    enabled,
    ...defaultQueryOptions,
  });
}

export function useGrantPlatformAdmin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: GrantPlatformAdmin) =>
      apiRequest<PlatformAdmin>('/api/v1/platform/admins', { method: 'POST', body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.platformAdmins });
      // A grant-by-invite creates a new account, which the users picker and
      // the Users admin page both need to see.
      void client.invalidateQueries({ queryKey: queryKeys.users });
    },
  });
}

export function useRevokePlatformAdmin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiRequest<void>(`/api/v1/platform/admins/${userId}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.platformAdmins }),
  });
}

/**
 * Creating and renaming the deployment's project.
 *
 * Plain tenant endpoints, called from the provider console. The provider role
 * holds `project.create` and `project.update` like any editor would, so there
 * is no provider-only project API to keep in step with this one -- see the note
 * on `PlatformController`.
 *
 * Both invalidate the session context as well as the project itself: the
 * shell's header carries the project's name, and after creating the first one
 * the whole `ProjectGate` verdict changes.
 */
function useProjectWriteInvalidation() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['projects'] });
    void client.invalidateQueries({ queryKey: queryKeys.authStatus });
    void client.invalidateQueries({ queryKey: ['platform'] });
  };
}

export function useCreateProject() {
  const invalidate = useProjectWriteInvalidation();
  return useMutation({
    mutationFn: (body: CreateProject) =>
      apiRequest<Project>('/api/v1/projects', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useUpdateProject(projectId: string) {
  const invalidate = useProjectWriteInvalidation();
  return useMutation({
    mutationFn: (body: UpdateProject) =>
      apiRequest<Project>(`/api/v1/projects/${projectId}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });
}
